import { Component, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UIRouter }         from '@uirouter/core';

import { CompanyMessagesModel }     from '../companies/models/company.messages';
import { ConfirmService }           from '../messages/confirm/confirm.service';
import { CustomerActionsComponent } from './customer.actions.component';
import { CustomerModel }            from './models/customer';
import { CustomersFiltersModel }    from './typings/customers.filters';
import { CustomersService }         from './customers.service';
import { MessagesService }          from '../messages/messages.service';
import { Table }                    from '../table/types/table';
import { TableQueryParameters }     from '../table/types/table-query-parameters';
import { TableService }             from '../table/table.service';

@Component({
  selector   : 'wor-customers',
  template: require('./customers.component.html')
})
export class CustomersComponent {
  @Input() companyMessages : CompanyMessagesModel = {} as CompanyMessagesModel;
  @Input() customers       : Array<CustomerModel> = [];

  filters : CustomersFiltersModel = this.tableService.setFiltersFromCache(this.customersService.tableCacheKey, {
      archivedOnly: false,
      value       : null
    });

  tableSettings : Table = {
    broadcast: this.customersService.broadcastKey,
    cache    : {
      enabled: true,
      key    : this.customersService.tableCacheKey
    },
    columns: [
      {
        field : 'display_name',
        header: {
          text: this.translateService.instant('CUSTOMER_SPACE.CUSTOMER')
        }
      },
      {
        field : 'mobile',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.MOBILE')
        },
        pipe: 'mobilePhone'
      },
      {
        field : 'phone',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.PHONE_NUMBER')
        },
        pipe: 'phone'
      },
      {
        component: {
          bindings: {
            outputs: {
              onEditClick           : ( customer : CustomerModel ) => this.viewCustomer(customer),
              onAddVehicleClick     : ( customer : CustomerModel ) => this.createVehicle(customer),
              onBookingClick        : ( customer : CustomerModel ) => this.createBooking(customer),
              onInvoiceClick        : ( customer : CustomerModel ) => this.createInvoice(customer),
              onCustomerPaymentClick: ( customer : CustomerModel ) => this.createCustomerPayment(customer),
              onSMSClick            : ( customer : CustomerModel ) => this.sendSms(customer),
              onEmailClick          : ( customer : CustomerModel ) => this.sendEmail(customer),
              onActivateClick       : ( customer : CustomerModel ) => this.activate(customer)
            }
          },
          enabled: true,
          factory: CustomerActionsComponent
        },
        sortable: false,
      }
    ],
    empty: {
      text: this.translateService.instant('QUERY_DROPDOWN_SPACE.GRID.NO_CUSTOMERS_FOUND')
    },
    pagination: {
      enabled   : true,
      pageSize  : this.tableService.getCachedPaging('customers').pageSize || 10,
      pageSizes : [ 10, 25, 50 ],
      queryCount: () => this.customersService.getCount(this.filters)
    },
    rows: {
      highlight: {
        enabled: true
      }
    },
    sorting: this.tableService.setSortingFromCache('customers', {
      direction: 'asc',
      field    : 'display_name'
    })
  };

  constructor (
    private confirmService   : ConfirmService,
    private customersService : CustomersService,
    private messagesService  : MessagesService,
    private router           : UIRouter,
    private tableService     : TableService,
    private translateService : TranslateService
  ) {}

  activate ( customer : CustomerModel ) : void {
    this.confirmService.generic(this.translateService.instant('JS_SPACE.INTEGRATIONS.ACTIVATE', {
      name: customer.display_name
    }))
    .then(() => this.customersService.activate(customer))
    .then(() => {
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.CUSTOMER_REACTIVATED'), 'success');

      this.router.stateService.reload();
    });
  }

  createBooking ( customer : CustomerModel ) : void {
    if ( customer.deleted ) {
      return;
    }

     this.router.stateService.go('app.event', {
      customer_id: customer.id
    });
  }

  createCustomerPayment ( customer : CustomerModel ) : void {
    if ( customer.deleted ) {
      return;
    }

     this.router.stateService.go('app.customer-branch-payment', {
      customer_id: customer.id
    });
  }

  createInvoice ( customer : CustomerModel ) : void {
    if ( customer.deleted ) {
      return;
    }

     this.router.stateService.go('app.invoice', {
      customer_id: customer.id
    });
  }

  createVehicle ( customer : CustomerModel ) : void {
    if ( customer.deleted ) {
      return;
    }

     this.router.stateService.go('app.vehicle', {
      customer_id: customer.id
    });
  }

  filter () : void {
    window[ this.customersService.broadcastKey ](this.filters);
  }

  getCustomers ( params : TableQueryParameters ) : Promise<Array<CustomerModel>> {
    return this.customersService.get(this.filters, params)
      .then(( customers : Array<CustomerModel> ) => {
        this.customers = customers;

        return this.customers;
      });
  }

  sendEmail ( customer : CustomerModel ) : void {
    this.customersService.sendEmail(customer, this.companyMessages.customer_email_message)
    .then(() => this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.MESSAGE_SENT'), 'success'))
    .catch(error => this.messagesService.error(error));
  }

  sendSms ( customer : CustomerModel ) : void {
    this.customersService.sendSms(customer)
    .then(() => this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.MESSAGE_SENT'), 'success'))
    .catch(error => this.messagesService.error(error));
  }

  viewCustomer ( customer : CustomerModel ) : void {
    if ( customer.deleted ) {
      return;
    }

    this.router.stateService.go('app.customer', {
      id: customer.id
    });
  }
}
